"use client";

import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";
import Image from "next/image";
import { ArrowLeft, ArrowRight, ExternalLink, Maximize2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import HomeSection from "@/components/marketing/HomeSection";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RESUME_TEMPLATES } from "@/lib/domain/resume-templates";

type ResumeTemplate = (typeof RESUME_TEMPLATES)[number];
// Dev's image optimizer can abort its internal fetch during hot reloads and navigation.
// Serve these small local previews directly in dev; keep responsive optimization in production.
const bypassDevImageOptimizer = process.env.NODE_ENV === "development";

export function ResumeTemplatesSection() {
  const { t } = useTranslation();
  const galleryRef = useRef<HTMLDivElement>(null);
  const cardStepRef = useRef(0);
  const [position, setPosition] = useState({ atStart: true, atEnd: false });
  const [selectedTemplate, setSelectedTemplate] = useState<ResumeTemplate | null>(null);

  const updatePosition = useCallback(() => {
    const gallery = galleryRef.current;
    if (!gallery) return;
    const atStart = gallery.scrollLeft <= 1;
    const atEnd = gallery.scrollLeft + gallery.clientWidth >= gallery.scrollWidth - 1;
    setPosition((current) => current.atStart === atStart && current.atEnd === atEnd
      ? current
      : { atStart, atEnd });
  }, []);

  useEffect(() => {
    const gallery = galleryRef.current;
    if (!gallery) return;
    const firstCard = gallery.firstElementChild;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === firstCard) {
          const gap = Number.parseFloat(getComputedStyle(gallery).columnGap) || 0;
          cardStepRef.current = entry.contentRect.width + gap;
        }
      }
      updatePosition();
    });
    observer.observe(gallery);
    if (firstCard) observer.observe(firstCard);
    return () => observer.disconnect();
  }, [updatePosition]);

  const scrollGallery = (direction: -1 | 1) => {
    const gallery = galleryRef.current;
    if (!gallery || !cardStepRef.current) return;
    gallery.scrollBy({
      left: direction * cardStepRef.current,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth",
    });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      scrollGallery(event.key === "ArrowLeft" ? -1 : 1);
    }
  };

  return (
    <section
      id="resume-templates"
      aria-labelledby="resume-templates-title"
      className="relative min-w-0 rounded-[2.25rem] border border-white/55 bg-white/45 px-5 py-8 backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.04] sm:px-8 sm:py-10 lg:px-12 lg:py-12"
    >
      <HomeSection
        badgeText="HOME_TEMPLATES_BADGE"
        title="HOME_TEMPLATES_TITLE"
        subtitle="HOME_TEMPLATES_DESC"
        titleId="resume-templates-title"
        titleSize="compact"
        titleStyle="editorial"
        align="left"
        subtitlePosition="aside"
      />

      <div className="mb-5 mt-8 flex items-center justify-between gap-4 sm:mt-10">
        <p id="resume-templates-hint" className="text-sm text-slate-600 dark:text-slate-300">{t("HOME_TEMPLATES_HINT")}</p>
        <div className="flex shrink-0 gap-2">
          <Button type="button" variant="outline" size="icon" className="rounded-full border-slate-300 bg-white/70 dark:border-white/20 dark:bg-white/5" aria-label={t("HOME_TEMPLATES_PREVIOUS")} aria-controls="resume-templates-gallery" disabled={position.atStart} onClick={() => scrollGallery(-1)}>
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button type="button" variant="outline" size="icon" className="rounded-full border-slate-300 bg-white/70 dark:border-white/20 dark:bg-white/5" aria-label={t("HOME_TEMPLATES_NEXT")} aria-controls="resume-templates-gallery" disabled={position.atEnd} onClick={() => scrollGallery(1)}>
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>

      <div
        id="resume-templates-gallery"
        ref={galleryRef}
        role="region"
        aria-roledescription={t("HOME_TEMPLATES_CAROUSEL")}
        aria-label={t("HOME_TEMPLATES_GALLERY")}
        aria-describedby="resume-templates-hint"
        tabIndex={0}
        onScroll={updatePosition}
        onKeyDown={handleKeyDown}
        className="flex snap-x snap-mandatory gap-5 overflow-x-auto overscroll-x-contain rounded-2xl pb-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-4"
      >
        {RESUME_TEMPLATES.map((template, index) => (
          <article key={template.layout} className="w-[85%] max-w-[340px] shrink-0 snap-start rounded-2xl border border-white/70 bg-white/75 p-4 dark:border-white/10 dark:bg-slate-900/60 sm:w-[calc((100%-1.25rem)/2)] lg:w-[calc((100%-2.5rem)/3)]">
            <div className="mb-4 flex items-center justify-between gap-2">
              <h3 className="text-base font-bold tracking-tight text-slate-900 dark:text-white">{t(template.nameKey)}</h3>
              {template.premium && <span className="rounded-full bg-indigo-50 px-2 py-1 text-[10px] font-semibold text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-200">{t("THEMEFORM_LAYOUT_PREMIUM_BADGE_TEXT")}</span>}
            </div>
            <button
              type="button"
              aria-label={t("HOME_TEMPLATES_OPEN_PREVIEW", { template: t(template.nameKey) })}
              onClick={() => setSelectedTemplate(template)}
              className="group relative block aspect-[612/792] w-full overflow-hidden rounded-sm bg-white shadow-[0_8px_24px_-12px_rgba(15,23,42,0.3)] ring-1 ring-slate-200 transition hover:ring-indigo-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              <Image src={`/resume-templates/previews/${template.layout}.jpg`} alt={t("HOME_TEMPLATES_PREVIEW_ALT", { template: t(template.nameKey) })} fill sizes="(max-width: 639px) 75vw, (max-width: 1023px) 40vw, 310px" className="object-contain" unoptimized={bypassDevImageOptimizer} />
              <span className="absolute bottom-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white/95 text-slate-600 shadow-sm transition group-hover:text-indigo-600"><Maximize2 className="h-4 w-4" aria-hidden="true" /></span>
            </button>
            <p className="mt-4 text-xs font-medium uppercase tracking-[0.12em] text-slate-700 dark:text-slate-300">{String(index + 1).padStart(2, "0")} / {String(RESUME_TEMPLATES.length).padStart(2, "0")}</p>
          </article>
        ))}
      </div>

      <Dialog open={selectedTemplate !== null} onOpenChange={(open) => { if (!open) setSelectedTemplate(null); }}>
        {selectedTemplate && (
          <DialogContent className="max-h-[90dvh] w-[calc(100%-2rem)] max-w-3xl overflow-y-auto rounded-3xl border-slate-300 bg-gradient-to-br from-slate-50 to-white p-5 dark:border-white/15 dark:from-slate-950 dark:to-slate-900 sm:p-6">
            <DialogHeader className="pr-7">
              <DialogTitle>{t(selectedTemplate.nameKey)}</DialogTitle>
              <DialogDescription>{t(selectedTemplate.descriptionKey)}</DialogDescription>
            </DialogHeader>
            <div className="relative aspect-[612/792] w-full bg-white shadow-sm ring-1 ring-slate-200">
              <Image src={`/resume-templates/previews/${selectedTemplate.layout}.jpg`} alt={t("HOME_TEMPLATES_PREVIEW_ALT", { template: t(selectedTemplate.nameKey) })} fill sizes="(max-width: 767px) 90vw, 720px" className="object-contain" unoptimized={bypassDevImageOptimizer} />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="max-w-sm text-xs text-slate-500 dark:text-slate-400">{t("HOME_TEMPLATES_SAMPLE_NOTE")}</p>
              <Button asChild variant="outline" className="rounded-full border-slate-300 bg-white/80 dark:border-white/20 dark:bg-white/5">
                <a href={`/resume-templates/previews/${selectedTemplate.layout}.pdf`} target="_blank" rel="noopener noreferrer">{t("HOME_TEMPLATES_OPEN_PDF")}<ExternalLink className="ml-2 h-3.5 w-3.5" aria-hidden="true" /></a>
              </Button>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </section>
  );
}
